import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { MediaPreview } from './MediaPreview';
import type { BriefDocument, MediaAsset } from '../../../../shared/types';
import { SIGNED_URL_QUERY_OPTIONS, signedUrlRefetchMs } from '../../lib/signed-url-refresh';

export function ReviewAttachments({ brandId, campaignId, briefId, document, editable, onSaved }: {
  brandId: string; campaignId: string; briefId: string; document: BriefDocument; editable: boolean; onSaved: () => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const ids = Array.isArray(document.metadata?.review_attachment_ids) ? document.metadata.review_attachment_ids as string[] : [];
  const library = useQuery({
    queryKey: ['review-attachment-library', brandId],
    queryFn: async () => {
      const assets: MediaAsset[] = []; let cursor: string | undefined;
      do {
        const page = await api.get<{ data: MediaAsset[]; next_cursor?: string }>(`/api/brands/${brandId}/media?limit=200${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`);
        assets.push(...page.data); cursor = page.next_cursor;
      } while (cursor);
      return assets;
    },
    enabled: open || ids.length > 0,
    ...SIGNED_URL_QUERY_OPTIONS,
    refetchInterval: query => signedUrlRefetchMs((query.state.data ?? []).map(asset => asset.url_expires_at)),
  });
  async function save(next: string[]) {
    setBusy(true); setError(null);
    try {
      await api.put(`/api/campaigns/${campaignId}/briefs/${briefId}/attachments`, { asset_ids: next, expected_document_key: document.document_key });
      await onSaved();
      await qc.invalidateQueries({ queryKey: ['brand-review'] });
      await qc.invalidateQueries({ queryKey: ['campaigns'] });
      await qc.invalidateQueries({ queryKey: ['brand-review-approved'] });
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not save attachments'); }
    finally { setBusy(false); }
  }
  const assets = library.data ?? [];
  return <section aria-label="Review attachments" className="space-y-3 border-t pt-3">
    <p className="text-sm font-semibold">Review attachments</p>
    <p className="text-xs text-slate-500">Files for review only. Attaching a file does not publish it. Changes require a new approval.</p>
    {error && <p role="alert">{error}</p>}
    {library.isError && <p role="alert">Attachments could not be loaded.</p>}
    {ids.map(id => {
      const asset = assets.find(item => item.id === id);
      return <div key={id} className="space-y-2">
        {asset?.thumbnail_url ? <MediaPreview mimeType={asset.mime_type} url={asset.thumbnail_url} label={asset.label || 'Review attachment'} /> : <p role="status">{library.isPending ? 'Loading attachment…' : 'Attachment is unavailable or deleted.'}</p>}
        {editable && <button type="button" disabled={busy} onClick={() => void save(ids.filter(value => value !== id))}>Detach {asset?.label || 'attachment'}</button>}
      </div>;
    })}
    {editable && <button type="button" onClick={() => setOpen(!open)} disabled={busy}>{open ? 'Close attachment picker' : 'Attach from brand library'}</button>}
    {open && <div>{library.isPending ? <p>Loading brand files…</p> : assets.filter(asset => !ids.includes(asset.id)).map(asset => <button className="block text-blue-700" key={asset.id} type="button" disabled={busy} onClick={() => void save([...ids, asset.id])}>Attach {asset.label || asset.id} ({asset.mime_type})</button>)}</div>}
  </section>;
}
